import useEForm from '@/hooks/useEform'
import className from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import InfoIcon from '@/assets/svgs/information-circle.svg?react'
import FormField from './EFormFieldDictionary'

/**
 * Ported from pitstop-ui/src/components/EForm/BaseEform.jsx
 *
 * Stripped (non-existent in submittals-ui):
 *   - AJV + custom-ajv-formats (not installed)
 *   - useHealthdex, useFeature, featureConstant
 *   - eFormReadMode utilities (extractPreviousValueFromPreviousPayload, parentPropsToChangedField)
 *   - eform-auto-filled-paths (parentPropsToPath, isPathLocked)
 *   - error-hint.svg (not in submittals-ui/src/assets/svgs)
 * Simplified:
 *   - effectiveIsReadOnly = isReadOnly (no auto-fill or status-lock logic)
 *   - validateSchema is a no-op (no AJV)
 *   - updatePayload is a no-op (read-only; setPayload not passed from page)
 *   - ErrorHint removed (depends on missing SVG + missing feature flags)
 */

const FormLabel = ({ required, label, description, propertyName }) => {
  const tooltipId = description && description !== label ? `field-desc-${propertyName}` : null
  return (
    <div className="flex flex-row items-center justify-start mb-1.25 font-black">
      {required && (
        <span className="h-4 mr-1 font-black text-red-50" data-testid="eform-label-asterisk">
          *
        </span>
      )}
      <div
        className="text-xs leading-4 font-normal text-grey-50 text-left"
        data-testid={`eformv2-label-${propertyName}`}
        id={propertyName}
      >
        {label}
      </div>
      {tooltipId && (
        <>
          <span
            className="inline-flex items-center ml-1 cursor-default [&_svg]:w-[13px] [&_svg]:h-[13px] [&_svg]:text-grey-50 hover:[&_svg]:text-grey-10"
            data-tooltip-id={tooltipId}
          >
            <InfoIcon />
          </span>
          <ReactTooltip
            id={tooltipId}
            content={description}
            className="w-[280px]! whitespace-normal! leading-4! text-left! text-[12px]!"
            place="top"
            opacity={1}
            noArrow
          />
        </>
      )}
    </div>
  )
}

FormLabel.propTypes = {
  required: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  propertyName: PropTypes.string,
}

const BaseEForm = ({ schema, required, parentProps = [], payload, isReadOnly = true }) => {
  const { getFieldValue } = useEForm()
  const [inputProps, setInputProps] = useState(null)
  const propertyName = parentProps[parentProps.length - 1]

  const schemaType = schema['x-uitype'] ?? schema.type
  const currentType = Array.isArray(schemaType) ? schemaType.find(d => d !== 'null') : schemaType
  const label = schema.title ?? schema.description

  const effectiveIsReadOnly = isReadOnly

  // No-op in read-only mode — kept so cells can call it without checking
  function updatePayload() {}
  function validateSchema() {
    return true
  }
  function setEFormFieldError() {}
  function setIsValidFormat() {}

  if (isReadOnly && schemaType === 'file-upload') return null

  const cellClassName = className({
    'w-full! pointer-events-none': isReadOnly,
  })

  const props = {
    error: false,
    isReadOnly: effectiveIsReadOnly,
    schema,
    payload,
    parentProps,
    inputProps,
    setInputProps,
    updatePayload,
    validateSchema,
    getFieldValue,
    setEFormFieldError,
    setIsValidFormat,
    required,
    propertyName,
  }

  return (
    <div data-testid="eform-row" className="w-full">
      {currentType === 'dynamic-dropdown' && (
        <div className="pt-4 mb-4 type-header-4 text-grey-10 border-t border-grey-90">{label}</div>
      )}
      <FormLabel
        required={required}
        label={currentType === 'dynamic-dropdown' ? `List of Templates` : label}
        description={currentType !== 'dynamic-dropdown' ? schema.description : undefined}
        propertyName={propertyName}
      />
      <div
        className={cellClassName}
        data-testid={`field-value-${propertyName}`}
        style={currentType !== 'dynamic-dropdown' ? { width: '95%' } : undefined}
      >
        <FormField type={currentType} schema={schema} {...props} />
      </div>
    </div>
  )
}

BaseEForm.propTypes = {
  schema: PropTypes.object,
  required: PropTypes.bool,
  parentProps: PropTypes.array,
  payload: PropTypes.object,
  isReadOnly: PropTypes.bool,
}

export default BaseEForm
